import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useConsignees } from './useConsignees'
import { SALES_KEY } from './useSales'
import { CONSIGNEE_INVENTORY_KEY } from './useConsigneeInventory'
import { useToast } from '../components/ToastContext'
import type { Sale } from '../lib/database.types'

/**
 * Owner-side hook. Subscribes to INSERTs on the sales table where
 * consignee_id is one of the owner's consignees. When mom logs a sale
 * on her phone, this fires a toast on the owner's open browser tab and
 * invalidates the relevant caches so the dashboards refresh in place.
 *
 * No-op when the owner has no consignees yet.
 */
export function useRealtimeConsigneeSales(): void {
  const queryClient = useQueryClient()
  const { data: consignees } = useConsignees()
  const { toast } = useToast()

  useEffect(() => {
    if (!consignees || consignees.length === 0) return
    const activeIds = consignees
      .filter((c) => c.accepted_at && !c.revoked_at)
      .map((c) => c.id)
    if (activeIds.length === 0) return

    const channel = supabase
      .channel('owner-consignee-sales')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sales',
          filter: `consignee_id=in.(${activeIds.join(',')})`,
        },
        (payload) => {
          const sale = payload.new as Sale
          const consignee = consignees.find((c) => c.id === sale.consignee_id)
          const name = consignee?.name ?? 'Consignee'
          toast({
            tone: 'success',
            text: `${name} just sold for $${Number(sale.gross_amount).toFixed(2)}`,
          })
          queryClient.invalidateQueries({ queryKey: SALES_KEY })
          queryClient.invalidateQueries({ queryKey: CONSIGNEE_INVENTORY_KEY })
          queryClient.invalidateQueries({ queryKey: ['products'] })
        },
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [consignees, queryClient, toast])
}
